import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faNewspaper,
  faEnvelope,
  faPhone,
  faMapMarkerAlt,
  faPaperPlane,
  faCheckCircle,
  faExclamationCircle,
  faFire,
  faBookmark
} from '@fortawesome/free-solid-svg-icons';
import { apiService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import './Contact.css';

function Contact() {
  const { isAuthenticated } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setErrors({});
    setSuccess(false);

    try {
      await apiService.submitContact(formData);
      setSuccess(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });
      
      // Scroll to success message
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      if (err.response?.data?.errors) {
        setErrors(err.response.data.errors);
      } else {
        setError(err.response?.data?.message || 'Failed to send message. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Contact Us - Bonlineco Blog</title>
        <meta name="description" content="Get in touch with our Magento experts. We're here to help you with your e-commerce needs." />
        <meta name="keywords" content="contact, bonlineco, magento experts, e-commerce support" />
        <meta property="og:title" content="Contact Us - Bonlineco Blog" />
        <meta property="og:description" content="Get in touch with our Magento experts." />
        <meta property="og:url" content="https://blog.bonlineco.com/contact" />
        <link rel="canonical" href="https://blog.bonlineco.com/contact" />
      </Helmet>

      <div className="contact-page">
        {/* Header */}
        <header className="contact-header">
          <div className="contact-container">
            <div className="contact-header-content">
              <div className="contact-logo">
                <FontAwesomeIcon icon={faNewspaper} className="contact-logo-icon" />
                <h1>Bonlineco Blog</h1>
              </div>
              
              <nav className="contact-nav">
                <a href="/" className="contact-nav-link">
                  <FontAwesomeIcon icon={faFire} />
                  Home
                </a>
                <a href="https://bonlineco.com" className="contact-nav-link" target="_blank" rel="noopener noreferrer">
                  <FontAwesomeIcon icon={faNewspaper} />
                  Services
                </a>
                <a href="https://store.bonlineco.com" className="contact-nav-link" target="_blank" rel="noopener noreferrer">
                  <FontAwesomeIcon icon={faBookmark} />
                  Store
                </a>
                <a href="/contact" className="contact-nav-link">
                  <FontAwesomeIcon icon={faEnvelope} />
                  Contact
                </a>
                {isAuthenticated && (
                  <a href="/admin_v3jq5z/dashboard" className="contact-nav-link contact-nav-link-admin">
                    <FontAwesomeIcon icon={faBookmark} />
                    Admin
                  </a>
                )}
              </nav>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="contact-main">
          <div className="contact-container">
            {/* Hero Section */}
            <div className="contact-hero">
              <h1 className="contact-hero-title">Get in Touch</h1>
              <p className="contact-hero-subtitle">
                Have a question or need help with your Magento store? Our experts are here to assist you.
              </p>
            </div>

            {/* Success Message */}
            {success && (
              <div className="contact-alert contact-alert-success">
                <FontAwesomeIcon icon={faCheckCircle} />
                <div>
                  <h4>Message Sent Successfully!</h4>
                  <p>Thank you for contacting us. We'll get back to you within 24-48 hours.</p>
                </div>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="contact-alert contact-alert-error">
                <FontAwesomeIcon icon={faExclamationCircle} />
                <div>
                  <h4>Error</h4>
                  <p>{error}</p>
                </div>
              </div>
            )}

            <div className="contact-content">
              {/* Contact Form */}
              <div className="contact-form-section">
                <h2>Send us a Message</h2>
                <form onSubmit={handleSubmit} className="contact-form">
                  <div className="contact-form-group">
                    <label htmlFor="name">Full Name *</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className={errors.name ? 'error' : ''}
                      required
                    />
                    {errors.name && <span className="contact-error-text">{errors.name[0]}</span>}
                  </div>

                  <div className="contact-form-group">
                    <label htmlFor="email">Email Address *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className={errors.email ? 'error' : ''}
                      required
                    />
                    {errors.email && <span className="contact-error-text">{errors.email[0]}</span>}
                  </div>

                  <div className="contact-form-group">
                    <label htmlFor="phone">Phone Number (Optional)</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className={errors.phone ? 'error' : ''}
                    />
                    {errors.phone && <span className="contact-error-text">{errors.phone[0]}</span>}
                  </div>

                  <div className="contact-form-group">
                    <label htmlFor="subject">Subject *</label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      className={errors.subject ? 'error' : ''}
                      required
                    />
                    {errors.subject && <span className="contact-error-text">{errors.subject[0]}</span>}
                  </div>

                  <div className="contact-form-group">
                    <label htmlFor="message">Message *</label>
                    <textarea
                      id="message"
                      name="message"
                      rows="6"
                      value={formData.message}
                      onChange={handleChange}
                      className={errors.message ? 'error' : ''}
                      required
                    ></textarea>
                    {errors.message && <span className="contact-error-text">{errors.message[0]}</span>}
                  </div>

                  <button type="submit" className="contact-submit-btn" disabled={loading}>
                    <FontAwesomeIcon icon={faPaperPlane} />
                    {loading ? 'Sending...' : 'Send Message'}
                  </button>
                </form>
              </div>

              {/* Contact Info */}
              <div className="contact-info-section">
                <h2>Contact Information</h2>
                <div className="contact-info-cards">
                  <div className="contact-info-card">
                    <div className="contact-info-icon">
                      <FontAwesomeIcon icon={faEnvelope} />
                    </div>
                    <h3>Email</h3>
                    <p>info@bonlineco.com</p>
                    <a href="mailto:info@bonlineco.com" className="contact-info-link">
                      Send Email
                    </a>
                  </div>

                  <div className="contact-info-card">
                    <div className="contact-info-icon">
                      <FontAwesomeIcon icon={faPhone} />
                    </div>
                    <h3>Phone</h3>
                    <p>Available on request</p>
                    <a href="/contact" className="contact-info-link">
                      Request Call
                    </a>
                  </div>

                  <div className="contact-info-card">
                    <div className="contact-info-icon">
                      <FontAwesomeIcon icon={faMapMarkerAlt} />
                    </div>
                    <h3>Location</h3>
                    <p>Remote Services</p>
                    <p className="contact-info-small">Worldwide Coverage</p>
                  </div>
                </div>

                <div className="contact-services">
                  <h3>Our Services</h3>
                  <ul>
                    <li>Magento Development</li>
                    <li>E-commerce Consulting</li>
                    <li>Custom Extensions</li>
                    <li>Performance Optimization</li>
                    <li>Migration Services</li>
                    <li>Maintenance & Support</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </main>

        {/* Footer */}
        <footer className="contact-footer">
          <div className="contact-container">
            <div className="contact-footer-content">
              <div className="contact-footer-brand">
                <FontAwesomeIcon icon={faNewspaper} className="contact-footer-icon" />
                <h4>Bonlineco Blog</h4>
                <p>Expert Magento solutions for your e-commerce success</p>
              </div>
              <div className="contact-footer-links">
                <div className="contact-footer-section">
                  <h5>Quick Links</h5>
                  <a href="/">Home</a>
                  <a href="/contact">Contact</a>
                  <a href="https://bonlineco.com" target="_blank" rel="noopener noreferrer">Services</a>
                  <a href="https://store.bonlineco.com" target="_blank" rel="noopener noreferrer">Store</a>
                </div>
              </div>
            </div>
            <div className="contact-footer-bottom">
              <p>&copy; {new Date().getFullYear()} Bonlineco Blog. All rights reserved.</p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}

export default Contact;
